/**
 * The verb → glyph dictionary.
 *
 * One icon per verb, everywhere. "Save" is always the tick, "Delete" always
 * the bin, "Export" always the arrow into the tray — so after two screens a
 * person reads the action by the glyph before the word (WCAG 3.2.4, consistent
 * identification). A verb that is not here gets NO icon rather than an
 * improvised one; add the verb and its glyph here (and in
 * i18n/en/lexicon.json → CTA.ICONS, which mirrors this file for prompts).
 *
 * Glyph names are keys of the Icon registry (icon.element.jsx).
 */
export const BUTTON_ICONS = Object.freeze({
  // the spine
  start: 'play',
  continue: 'arrowRight',
  next: 'arrowRight',
  back: 'arrowLeft',
  // Pagination reads in pairs. `next` had its arrow and its opposite had
  // nothing, on all fourteen of them.
  previous: 'arrowLeft',
  prev: 'arrowLeft',
  // create / change
  save: 'check',
  add: 'plus',
  new: 'plus',
  create: 'plus',
  edit: 'edit',
  delete: 'trash',
  remove: 'close',
  clear: 'close',
  cancel: 'close',
  close: 'close',
  copy: 'copy',
  // move data
  export: 'download',
  download: 'download',
  upload: 'upload',
  import: 'upload',
  share: 'share',
  send: 'send',
  resend: 'send',
  publish: 'publish',
  unpublish: 'unpublish',
  // review
  review: 'eye',
  view: 'eye',
  preview: 'eye',
  open: 'open',
  reopen: 'open',
  claim: 'userCheck',
  approve: 'check',
  accept: 'check',
  reject: 'close',
  decline: 'close',
  // money
  'top up': 'wallet',
  pay: 'creditCard',
  buy: 'creditCard',
  // find / run
  search: 'search',
  filter: 'filter',
  refresh: 'refresh',
  retry: 'refresh',
  reload: 'refresh',
  // 🔁 The same three verbs people actually type for "do it again". `retry`
  // was in the dictionary and `try` was not, so every one of the ten "Try
  // again" buttons in the app went out without the glyph its synonym has.
  try: 'refresh',
  reset: 'refresh',
  run: 'play',
  generate: 'sparkles',
  regenerate: 'sparkles',
  // people
  invite: 'userPlus',
  apply: 'userPlus',
  'sign in': 'logIn',
  'log in': 'logIn',
  'sign out': 'logout',
  'log out': 'logout',
  exit: 'logout',
  settings: 'settings',
});

const norm = (s) => String(s).trim().toLowerCase().replace(/[.!…]+$/, '');

/**
 * The glyph for a button label, or null. Matches the whole label first
 * ("Top up"), then its first word ("Save changes" → save), so the icon is
 * right even before a long label is shortened to one word.
 */
export const iconForLabel = (label) => {
  if (typeof label !== 'string') return null;
  const whole = norm(label);
  if (!whole) return null;
  if (BUTTON_ICONS[whole]) return BUTTON_ICONS[whole];
  const twoWords = whole.split(/\s+/).slice(0, 2).join(' ');
  if (BUTTON_ICONS[twoWords]) return BUTTON_ICONS[twoWords];
  const first = whole.split(/\s+/)[0];
  return BUTTON_ICONS[first] || null;
};

export default BUTTON_ICONS;
